import React from 'react';
import { SectionHeading } from './SectionHeading';

/**
 * BLOCK: faq
 * ACF layout name: `block_faq`
 *
 * Fields:
 *  - eyebrow  (text)
 *  - heading  (text)
 *  - faqs     (repeater: question, answer)
 *
 * Pair every instance with `faqSchema()` in the page's useSeo call. FAQ markup
 * is the single strongest signal for generative search engines (GEO): AI
 * assistants quote question/answer pairs close to verbatim.
 *
 * Rendered as native <details> so it works without JavaScript, stays keyboard
 * accessible, and keeps every answer inside the crawlable DOM.
 */

export interface Faq {
  question: string;
  answer: string;
}
interface FaqSectionProps {
  eyebrow?: string;
  heading?: string;
  faqs: readonly Faq[];
  className?: string;
}
export function FaqSection({
  eyebrow = 'Good to know',
  heading = 'Common questions',
  faqs,
  className = ''
}: FaqSectionProps) {
  if (faqs.length === 0) return null;
  return <section aria-labelledby="faq-heading" className={className} data-id="element-71" data-mp="7z4aewp9yinl">
      <SectionHeading eyebrow={eyebrow} heading={heading} id="faq-heading" data-id="element-72" data-mp="ux89lnm1rj06" />
      <div className="mt-9 border-t border-cream-200" data-id="element-73" data-mp="yz24tgdh3usy">
        {faqs.map(faq => <details key={faq.question} className="group border-b border-cream-200 py-5" data-id="element-74" data-mp="z0why8ii6imh">
            <summary className="flex cursor-pointer list-none items-center justify-between gap-6 font-serif text-xl text-charcoal marker:hidden focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-700 focus-visible:ring-offset-4 sm:text-2xl" data-id="element-75" data-mp="q68d2jgub2y7">
              <span data-id="element-76" data-mp="g6hiyiw6ypyn">{faq.question}</span>
              <span aria-hidden="true" className="relative mt-1 flex h-5 w-5 shrink-0 items-center justify-center rounded-full border border-sage/60 text-sage-700 transition-transform group-open:rotate-45" data-id="element-77" data-mp="29cj50n2mnbu">
                <span className="absolute h-px w-2.5 bg-current" data-id="element-78" data-mp="1wkr5d7gvi4mu" />
                <span className="absolute h-2.5 w-px bg-current" data-id="element-79" data-mp="21klylho4f6b2" />
              </span>
            </summary>
            <p className="mt-4 max-w-2xl text-base font-light leading-relaxed text-charcoal-50" data-id="element-80" data-mp="1z8tigdyfw0x0">
              {faq.answer}
            </p>
          </details>)}
      </div>
    </section>;
}